import React from 'react';

import { ContinueButton } from './ContinueButton';
import { EmailInput } from './EmailInput';
import { EmailLoginForm } from './EmailLoginForm';
import { EmailRegisterForm } from './EmailRegisterForm';

interface ForgotSuccessMessageProps {
  email: string;
  onBackToLogin: () => void;
}

const ForgotSuccessMessage: React.FC<ForgotSuccessMessageProps> = ({ email, onBackToLogin }) => (
  <div className="email-section">
    <p className="forgot-success-message">
      If an account exists for {email}, we&apos;ve sent a password reset link. Please check your inbox.
    </p>
    <button type="button" className="forgot-back-link" onClick={onBackToLogin}>
      Back to Login
    </button>
  </div>
);

interface ForgotPasswordFormProps {
  email: string;
  error: string | null | undefined;
  isLoading: boolean;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onBackToLogin: () => void;
}

const handleFormSubmit = (e: React.FormEvent, onSubmit: () => void) => {
  e.preventDefault();
  onSubmit();
};

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  email,
  error,
  isLoading,
  onEmailChange,
  onSubmit,
  onBackToLogin,
}) => (
  <form className="email-section" onSubmit={(e) => handleFormSubmit(e, onSubmit)}>
    <p className="forgot-instruction">Enter your email and we&apos;ll send you a reset link.</p>
    <EmailInput value={email} onChange={onEmailChange} disabled={isLoading} />
    {error && <p className="forgot-error">{error}</p>}
    <ContinueButton onClick={onSubmit} isLoading={isLoading} disabled={!email.trim()}>
      Send Reset Link
    </ContinueButton>
    <button type="button" className="forgot-back-link" onClick={onBackToLogin}>
      Back to Login
    </button>
  </form>
);

interface EmailSectionRendererProps {
  forgotSuccess: boolean;
  mode: 'login' | 'forgot' | 'register';
  email: string;
  password: string;
  displayName: string;
  country: string;
  stateCode: string;
  step: 'email' | 'password';
  error: string | null | undefined;
  isLoading: boolean;
  forgotLoading: boolean;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setDisplayName: (displayName: string) => void;
  setCountry: (country: string) => void;
  setStateCode: (stateCode: string) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  handleBackToEmail: () => void;
  handleBackToLogin: () => void;
  handleForgotPassword: () => void;
  setMode: (mode: 'login' | 'forgot' | 'register') => void;
  handleSwitchMode: (newMode: 'login' | 'register') => void;
}

const renderForgotSuccess = (email: string, onBackToLogin: () => void) => (
  <ForgotSuccessMessage email={email} onBackToLogin={onBackToLogin} />
);

const renderForgotForm = (
  email: string,
  error: string | null | undefined,
  isLoading: boolean,
  setEmail: (email: string) => void,
  handleForgotPassword: () => void,
  handleBackToLogin: () => void
) => (
  <ForgotPasswordForm
    email={email}
    error={error}
    isLoading={isLoading}
    onEmailChange={setEmail}
    onSubmit={handleForgotPassword}
    onBackToLogin={handleBackToLogin}
  />
);

const renderRegisterForm = (props: EmailSectionRendererProps) => (
  <EmailRegisterForm
    email={props.email}
    password={props.password}
    displayName={props.displayName}
    country={props.country}
    stateCode={props.stateCode}
    error={props.error}
    isLoading={props.isLoading}
    onEmailChange={props.setEmail}
    onPasswordChange={props.setPassword}
    onDisplayNameChange={props.setDisplayName}
    onCountryChange={props.setCountry}
    onStateCodeChange={props.setStateCode}
    onSubmit={props.handleSubmit}
    onBackToLogin={() => props.handleSwitchMode('login')}
  />
);

const renderLoginForm = (props: EmailSectionRendererProps) => (
  <>
    <EmailLoginForm
      email={props.email}
      password={props.password}
      step={props.step}
      error={props.error}
      isLoading={props.isLoading}
      onEmailChange={props.setEmail}
      onPasswordChange={props.setPassword}
      onSubmit={props.handleSubmit}
      onBackToEmail={props.handleBackToEmail}
      onForgotPassword={() => props.setMode('forgot')}
    />
    <button
      type="button"
      className="forgot-password-link email-renderer-container"
      onClick={() => props.handleSwitchMode('register')}
    >
      Don&apos;t have an account? Sign up
    </button>
  </>
);

export const EmailSectionRenderer: React.FC<EmailSectionRendererProps> = (props) => {
  if (props.forgotSuccess) {
    return renderForgotSuccess(props.email, props.handleBackToLogin);
  }

  if (props.mode === 'forgot') {
    return renderForgotForm(
      props.email,
      props.error,
      props.forgotLoading,
      props.setEmail,
      props.handleForgotPassword,
      props.handleBackToLogin
    );
  }

  if (props.mode === 'register') {
    return renderRegisterForm(props);
  }

  return renderLoginForm(props);
};
